import { useState } from 'react';
import { Recipe } from '@/types/api';
import { ImageUtils } from '@/utils/imageUtils';
import { ImageIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

interface OptimizedImageProps {
  recipe: Recipe;
  alt?: string;
  className?: string;
  fallbackClassName?: string;
  showLoadingSpinner?: boolean;
}

export function OptimizedImage({
  recipe,
  alt,
  className,
  fallbackClassName,
  showLoadingSpinner = true
}: OptimizedImageProps) {
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const [currentSrc, setCurrentSrc] = useState(() => ImageUtils.getRecipeImageUrl(recipe));

  const handleImageError = () => {
    if (!imageError) {
      // Primera vez que falla - intentar con fallback inteligente
      ImageUtils.handleImageError(recipe, (fallbackUrl) => {
        setCurrentSrc(fallbackUrl);
        setImageError(true);
        setImageLoading(true); // Resetear loading para el fallback
      });
    } else {
      // Fallback también falló - mostrar placeholder
      setImageLoading(false);
    }
  };

  const handleImageLoad = () => {
    setImageLoading(false);
  };

  // Si ambas imágenes fallaron, mostrar placeholder
  if (imageError && !imageLoading && !currentSrc) {
    return (
      <div className={cn(
        "flex items-center justify-center bg-gray-100",
        fallbackClassName || className
      )}>
        <ImageIcon className="h-8 w-8 text-gray-400" />
      </div>
    );
  }

  return (
    <div className={cn("relative", className)}>
      {/* Loading spinner */}
      {imageLoading && showLoadingSpinner && (
        <div className={cn(
          "absolute inset-0 bg-gray-100 animate-pulse flex items-center justify-center",
          className
        )}>
          <ImageIcon className="h-8 w-8 text-gray-400" />
        </div>
      )}
      
      {/* Imagen principal */}
      <img
        src={currentSrc}
        alt={alt || recipe.title}
        className={cn(
          "w-full h-full object-cover transition-opacity duration-300",
          imageLoading ? "opacity-0" : "opacity-100",
          className
        )}
        onLoad={handleImageLoad}
        onError={handleImageError}
        loading="lazy"
      />
    </div>
  );
}

// Hook para usar la lógica de imagen optimizada
export function useOptimizedImage(recipe: Recipe) {
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const [currentSrc, setCurrentSrc] = useState(() => ImageUtils.getRecipeImageUrl(recipe));

  const handleImageError = () => {
    if (!imageError) {
      ImageUtils.handleImageError(recipe, (fallbackUrl) => {
        setCurrentSrc(fallbackUrl);
        setImageError(true);
        setImageLoading(true);
      });
    } else {
      setImageLoading(false);
    }
  };

  const handleImageLoad = () => {
    setImageLoading(false);
  };

  return {
    src: currentSrc,
    loading: imageLoading,
    error: imageError && !imageLoading,
    onLoad: handleImageLoad,
    onError: handleImageError,
  };
}
